import { useCallback, useEffect, useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { asArray } from "@/lib/as-array"
import {
  buildBoard,
  receiptsOf,
  sameBoard,
  taskId,
  type Board,
  type ColumnId,
} from "@/lib/tasks"
import {
  useDeleteTasksTaskId,
  useGetAuthUser,
  useGetTasks,
  usePatchTasksTaskIdStatus,
  usePostTasks,
  usePutTasksTaskId,
} from "openapi/api/endpoints"
import type { PostTasksBody, Task } from "openapi/api/model"
import { invalidateTaskQueries } from "@/lib/query-client"

/** Who a board is scoped to. */
export type TaskOwner = "everyone" | "mine"

/**
 * The Ops board — canvas 33a — on live data.
 *
 * Fetching, the optimistic board and every mutation live here; the board
 * component renders what it is handed. The page above keeps only the two
 * dialogs, because those own their own form state.
 *
 * One thing 33a asks for that the contract cannot answer: its filter reads
 * "Assigned to me", and a task on this board has no assignee. `assigned_to`
 * exists in the contract but only on *pipeline* tasks
 * (`POST /jobs/{jobId}/pipeline/tasks`), which are a different resource —
 * `GET /tasks` returns a `Task`, whose only person is `created_by`. So the
 * filter is offered over that, and named for what it actually does. Scoping to
 * "everyone" is the default, because the board shows every task today and
 * quietly hiding rows is not a restyle.
 */
export function useOpsTasks() {
  const tasksQuery = useGetTasks()
  const { data: currentUser } = useGetAuthUser()
  const queryClient = useQueryClient()

  const { mutateAsync: patchStatus } = usePatchTasksTaskIdStatus()
  const { mutateAsync: postTask } = usePostTasks()
  const { mutateAsync: putTask } = usePutTasksTaskId()
  const { mutateAsync: deleteTaskById } = useDeleteTasksTaskId()

  const [owner, setOwner] = useState<TaskOwner>("everyone")

  const all = useMemo(() => asArray<Task>(tasksQuery.data), [tasksQuery.data])

  const tasks = useMemo(() => {
    if (owner === "everyone") return all
    const me = currentUser?.id
    // Without a signed-in id there is nothing to compare against, so the
    // filter shows everything rather than an empty board.
    if (!me) return all
    return all.filter((task) => task.created_by === me)
  }, [all, currentUser?.id, owner])

  const byId = useMemo(() => {
    const map = new Map<string, Task>()
    for (const task of tasks) {
      const id = taskId(task)
      if (id) map.set(id, task)
    }
    return map
  }, [tasks])

  /*
    The board is local so a drag lands before the request does. It is rebuilt
    from the server whenever the server's answer actually differs — comparing
    first, because replacing an equal board on every refetch would throw away
    the order a drag just produced.
  */
  const [board, setBoard] = useState<Board>(() => buildBoard(tasks))
  useEffect(() => {
    const next = buildBoard(tasks)
    setBoard((prev) => (sameBoard(prev, next) ? prev : next))
  }, [tasks])

  const refresh = useCallback(async () => {
    await invalidateTaskQueries(queryClient)
  }, [queryClient])

  /** Move a card between columns, which is the only thing a drag means here. */
  const move = useCallback(
    async (id: string, to: ColumnId, index: number) => {
      const previous = board
      const next: Board = {
        todo: [...board.todo],
        inProgress: [...board.inProgress],
        blocked: [...board.blocked],
        done: [...board.done],
      }
      for (const column of Object.keys(next) as ColumnId[]) {
        next[column] = next[column].filter((entry) => entry !== id)
      }
      next[to].splice(index, 0, id)
      setBoard(next)

      try {
        await patchStatus({ taskId: id, data: { status: to } })
        await refresh()
      } catch (error) {
        // Put the card back where it was; a board that keeps a move the server
        // refused is a board that lies about what the server holds.
        setBoard(previous)
        toast.error("The task could not be moved", { description: `${error}` })
      }
    },
    [board, patchStatus, refresh],
  )

  const create = useCallback(
    async (body: PostTasksBody) => {
      try {
        await postTask({ data: body })
        await refresh()
        toast.success("Task created")
      } catch (error) {
        toast.error("The task could not be created", { description: `${error}` })
        // Rethrown so the composer keeps the form rather than discarding a
        // completed one on a task that was never created.
        throw error
      }
    },
    [postTask, refresh],
  )

  const update = useCallback(
    async (id: string, updates: Partial<Task>) => {
      try {
        await putTask({ taskId: id, data: updates })
        await refresh()
        toast.success("Task updated")
      } catch (error) {
        toast.error("The task could not be updated", { description: `${error}` })
        throw error
      }
    },
    [putTask, refresh],
  )

  const destroy = useCallback(
    async (id: string) => {
      try {
        await deleteTaskById({ taskId: id })
        await refresh()
        toast.success("Task deleted")
      } catch (error) {
        toast.error("The task could not be deleted", { description: `${error}` })
        throw error
      }
    },
    [deleteTaskById, refresh],
  )

  return {
    board,
    byId,
    owner,
    setOwner,
    /*
      Receipts are computed from the *scoped* list, so the line and the columns
      under it are always counting the same tasks.
    */
    receipts: useMemo(() => receiptsOf(tasks), [tasks]),
    initials: initialsOf(currentUser?.name),
    /*
      Whether the board is known at all. Every figure here comes from
      `asArray(undefined)` until the request lands — an empty array, so the
      receipts read as a confident row of zeroes and the columns as a desk with
      nothing on it. The board says neither until it can.
    */
    isLoading: tasksQuery.isPending,
    isError: tasksQuery.isError,
    move,
    create,
    update,
    destroy,
  }
}

function initialsOf(name?: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
}
